// src/v2/VoiceSDK.js

import EventEmitter from '../shared/EventEmitter.js';

import AudioPlayer from './AudioPlayer.js';

import AudioRecorder from '../core/AudioRecorder.js';

import AudioFormatConverter from './utils/AudioFormatConverter.js';

import ClientToolsRegistry from '../core/ClientToolsRegistry.js';

import { ClientScriptManager } from '../core/ClientScriptManager.js';

import { captureScreenshot } from '../utils/screenshot.js';

import { registerVisualTools, extractPageContext } from '../utils/visual-tools.js';



/**

 * VoiceSDK v2 - Multi-codec speech-to-speech SDK

 * 

 * Features:

 * - Format negotiation with backend

 * - Support for PCM, PCMU (μ-law), PCMA (A-law)

 * - Support for WAV and raw audio containers

 * - Uses v2 protocol with format negotiation

 * 

 * Events:

 * - 'connected' - WebSocket connected

 * - 'disconnected' - WebSocket disconnected

 * - 'formatNegotiated' - Audio format confirmed by server

 * - 'recordingStarted' - Recording started

 * - 'recordingStopped' - Recording stopped

 * - 'playbackStarted' - Audio playback started

 * - 'playbackStopped' - Audio playback stopped

 * - 'error' - Error occurred

 * - 'message' - Text message from server
 *
 * - 'dataInjected' - Data injection successfully acknowledged by server
 *
 * - 'dataInjectionError' - Data injection failed (validation, timeout, or server error)
 *
 * - 'disclaimersRequired' - Server requires disclaimer acknowledgement (payload: texts, disclaimersHash, ...)
 * - 'disclaimerRejected' - Server closed or rejected disclaimer ({ code, message })

 */

class VoiceSDK_v2 extends EventEmitter {

  // Resume state storage key and TTL (single key for everything)
  static RESUME_STORAGE_KEY = 'voice_resume_state';
  static RESUME_TTL_MS = 10000; // 10 seconds

  // Supported INPUT audio format configurations (matching backend validation)

  static SUPPORTED_INPUT_ENCODINGS = ['pcm', 'pcmu', 'pcma'];

  static SUPPORTED_INPUT_SAMPLE_RATES = [8000, 16000, 22050, 24000, 44100, 48000];

  static SUPPORTED_INPUT_BIT_DEPTHS = [8, 16, 24];

  static SUPPORTED_INPUT_CHANNELS = [1];

  

  // Supported OUTPUT audio format configurations (matching backend validation)

  static SUPPORTED_OUTPUT_ENCODINGS = ['pcm', 'pcmu', 'pcma'];

  static SUPPORTED_OUTPUT_SAMPLE_RATES = [8000, 16000, 22050, 24000, 44100, 48000];

  static SUPPORTED_OUTPUT_BIT_DEPTHS = [8, 16, 24];

  static SUPPORTED_OUTPUT_CHANNELS = [1];

  static SUPPORTED_OUTPUT_CONTAINERS = ['raw', 'wav'];

  

  constructor(config = {}) {

    super();

    

    this.version = '2.0.0';

    

    this.config = {

      // Connection
      websocketUrl: config.websocketUrl || 'wss://speech.talktopc.com/ws/conv',

      agentId: config.agentId,

      appId: config.appId,

      

      // Input format (what we send to server)

      sampleRate: config.sampleRate || 16000,

      channels: config.channels || 1,

      bitDepth: config.bitDepth || 16,

      

      // Audio processor path (optional)

      audioProcessorPath: config.audioProcessorPath, // Will auto-detect if not provided

      

      // Output format (what we want from server)

      outputContainer: config.outputContainer || this.getDefaultContainer(config),

      outputEncoding: config.outputEncoding || config.outputCodec || 'pcm',

      outputSampleRate: config.outputSampleRate || 24000,

      outputChannels: config.outputChannels || 1,

      outputBitDepth: config.outputBitDepth || 16,

      // Output frame duration (for raw PCM streaming)
      outputFrameDurationMs: config.outputFrameDurationMs || 600, // Default 600ms - smooth playback with good latency balance

      

      // Legacy support

      outputCodec: config.outputCodec,

      

      // Optional settings

      agentSettingsOverride: config.agentSettingsOverride || null,

      visualAssistant: config.visualAssistant || null, // Visual assistant configuration

      variables: config.variables || null,

      autoReconnect: config.autoReconnect !== false,

      // Audio constraints for getUserMedia (optional)
      // If not provided, defaults will be used: echoCancellation: true, noiseSuppression: true, autoGainControl: true
      audioConstraints: config.audioConstraints || null,

      

      // Protocol version

      protocolVersion: config.protocolVersion || 2,

      // Flavor object (sent in hello message if set)
      flavor: config.flavor || null

    };

    

    // State

    this.isConnected = false;

    this.hasEverConnected = false; // Track if we've successfully connected at least once

    this.helloAckReceived = false;

    /** When true, server hello_ack requires disclaimer_ack before STT/greeting; block recording until cleared */
    this.disclaimersPending = false;

    /** Server copy hash (SHA-256) echoed in disclaimer_ack */
    this.disclaimersHash = null;

    /** Last server disclaimer payload (for UI) */
    this.lastDisclaimerPayload = null;

    /** Set when hangup() runs with an open socket — skip resume-key clear in destroy() */
    this._gracefulHangupInitiated = false;

    this.isRecording = false;

    this.isPlaying = false;

    this.isDestroyed = false;

    this.isPaused = false;

    this.outputAudioFormat = null;

    this.requestedOutputFormat = null; // What user requested

    this.formatConverter = null; // Format converter instance

    this.websocket = null;

    this.conversationId = null; // Conversation ID from server

    // Transcript tracking - transcripts arrive between sentences
    this.transcriptQueue = []; // Queue of transcripts for history
    this.currentPlayingSentenceId = null; // Sentence ID currently being played
    this.audioFrameCount = 0; // Track number of audio frames received
    this.lastAudioStartTime = 0; // Timestamp of last audio_start message (to prevent premature stop_playing)
    
    // Screen wake lock - keeps screen on during voice calls (mobile)
    this.wakeLock = null;
    this._wakeLockRequesting = false; // Flag to prevent race conditions
    
    // Data injection tracking - for programmatic data injection
    this.pendingInjections = new Map(); // Maps correlationId to { resolve, reject, timeout }
    this.injectionCounter = 0; // Counter for generating unique correlation IDs
    
    // Barge-in detection is handled by the backend, not the frontend
    // The backend analyzes the audio stream and sends 'barge_in' message when user interrupts
    // Frontend only responds to backend barge-in messages

    

    // Components

    this.audioPlayer = new AudioPlayer(this.config);

    // Pass connection check function to AudioRecorder so it can verify connection before starting VAD
    const recorderConfig = {
      ...this.config,
      checkConnection: () => {
        return this.isConnected && this.websocket && this.websocket.readyState === WebSocket.OPEN;
      }
    };
    this.audioRecorder = new AudioRecorder(recorderConfig);

    


    // DON'T set default format in constructor
    // Wait for hello_ack to set the correct format
    // outputAudioFormat is already set to null above

    

    this.setupAudioPlayerEvents();

    this.setupAudioRecorderEvents();

    
    // Initialize client tools registry. If the host (e.g. TTPChatWidget) passed a
    // sharedToolHandlers Map, register handlers into it so voice and text SDKs share
    // one source of truth — registering once via TTPChatWidget exposes the handler
    // to whichever channel is active at tool-call time.
    this.clientToolsRegistry = new ClientToolsRegistry(
      (message) => this.sendMessage(message),
      () => this.conversationId,
      config.sharedToolHandlers || null
    );

    // Flavor-independent client-tool script manager (reserved partner id
    // `__client_tools__`). Owns its own PartnerScriptRunner so client-tool
    // scripts run regardless of flavor, alongside (and separate from) the
    // ecommerce flavor's partner runner. Sends result/emit frames over the
    // voice WebSocket.
    this.clientScriptManager = new ClientScriptManager({
      send: (message) => this.sendMessage(message),
      label: 'voice',
    });

    // ===== REGISTER BUILT-IN SDK TOOLS =====
    // These tools work automatically - users don't need to implement them
    // Backend controls availability via agent configuration
    this._registerBuiltInTools();
    // ===== END BUILT-IN TOOLS =====

    // Validate input and output formats

    this.validateConfig();

    // Setup page unload handler for resume state storage
    this._setupPageUnloadHandler();

    

    

  }

  /**
   * Register built-in SDK tools that work automatically
   * These tools are always available - backend controls access via agent config
   * @private
   */
  _registerBuiltInTools() {
    try {
      registerVisualTools(this.clientToolsRegistry);
    } catch (error) {
      console.error('❌ VoiceSDK: Error registering built-in tools:', error);
      console.error('   Error details:', error.message, error.stack);
    }
  }

  /**
   * Get stored conversationId from sessionStorage
   * @returns {string|null} Conversation ID or null if not found/unavailable
   */
  getStoredConversationId() {
    try {
      if (typeof sessionStorage === 'undefined') {
        console.warn('⚠️ VoiceSDK v2: sessionStorage not available (private mode?)');
        return null;
      }
      const stored = sessionStorage.getItem(VoiceSDK_v2.RESUME_STORAGE_KEY);
      if (stored) {
        const state = JSON.parse(stored);
        if (state && state.conversationId) {
          console.log('📦 VoiceSDK v2: Found stored conversationId:', state.conversationId);
          return state.conversationId;
        }
      }
      return null;
    } catch (error) {
      console.warn('⚠️ VoiceSDK v2: Error reading sessionStorage:', error);
      return null;
    }
  }

  /**
   * Store conversationId in sessionStorage (updates resume state)
   * @param {string} conversationId - Conversation ID to store
   */
  storeConversationId(conversationId) {
    if (!conversationId) return;
    
    try {
      if (typeof sessionStorage === 'undefined') {
        console.warn('⚠️ VoiceSDK v2: sessionStorage not available (private mode?)');
        return;
      }
      
      // Get existing state or create new one
      let state = {};
      const existing = sessionStorage.getItem(VoiceSDK_v2.RESUME_STORAGE_KEY);
      if (existing) {
        try {
          state = JSON.parse(existing);
        } catch (e) {
          // Invalid JSON, start fresh
        }
      }
      
      // Update state with new conversationId and timestamp
      state.conversationId = conversationId;
      state.timestamp = Date.now();
      state.fromPage = state.fromPage || (window.location.pathname + window.location.search);
      
      sessionStorage.setItem(VoiceSDK_v2.RESUME_STORAGE_KEY, JSON.stringify(state));
      console.log('💾 VoiceSDK v2: Stored conversationId:', conversationId);
    } catch (error) {
      console.warn('⚠️ VoiceSDK v2: Error writing to sessionStorage:', error);
    }
  }

  /**
   * Clear stored conversationId from sessionStorage
   */
  clearStoredConversationId() {
    try {
      if (typeof sessionStorage === 'undefined') {
        return;
      }
      sessionStorage.removeItem(VoiceSDK_v2.RESUME_STORAGE_KEY);
      console.log('🗑️ VoiceSDK v2: Cleared stored conversationId');
    } catch (error) {
      console.warn('⚠️ VoiceSDK v2: Error clearing sessionStorage:', error);
    }
  }

  /**
   * Get resume state from sessionStorage if still valid (< 10 seconds old)
   * @returns {Object|null} Resume state or null if expired/not found
   * @private
   */
  _getResumeState() {
    try {
      if (typeof sessionStorage === 'undefined') {
        return null;
      }
      const stored = sessionStorage.getItem(VoiceSDK_v2.RESUME_STORAGE_KEY);
      if (!stored) return null;
      
      const state = JSON.parse(stored);
      const age = Date.now() - state.timestamp;
      
      if (age > VoiceSDK_v2.RESUME_TTL_MS) {
        console.log('[VoiceSDK] Resume state expired:', age, 'ms');
        this._clearResumeState();
        return null;
      }
      
      console.log('[VoiceSDK] Found valid resume state:', state, 'age:', age, 'ms');
      return state;
    } catch (e) {
      console.warn('[VoiceSDK] Failed to get resume state:', e);
      return null;
    }
  }

  /**
   * Clear resume state from sessionStorage
   * @private
   */
  _clearResumeState() {
    try {
      if (typeof sessionStorage === 'undefined') {
        return;
      }
      sessionStorage.removeItem(VoiceSDK_v2.RESUME_STORAGE_KEY);
    } catch (e) {
      // Ignore
    }
  }

  /**
   * Setup handler to store state before page unload
   * @private
   */
  _setupPageUnloadHandler() {
    // Store state before page navigates away
    window.addEventListener('beforeunload', () => {
      if (this.conversationId && this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.storeConversationId(this.conversationId);
      }
    });
    
    // Also handle visibilitychange for mobile/tab switching
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        // Re-acquire wake lock if we're still in a call
        if (this.isRecording && this.isConnected) {
          this._requestWakeLock();
        }
        
        // Resume AudioContext if suspended (browsers suspend it when tab is hidden)
        if (this.isRecording && this.audioRecorder) {
          this.audioRecorder.resumeAudioContext().catch(err => {
            console.error('VoiceSDK v2: Error resuming AudioContext on visibility change:', err);
          });
        }
        
        // Also resume audio playback context
        if (this.audioPlayer) {
          this.audioPlayer.resumeAudioContext().catch(err => {
            console.error('VoiceSDK v2: Error resuming playback AudioContext on visibility change:', err);
          });
        }
      }
      
      if (document.visibilityState === 'hidden') {
        if (this.conversationId && this.websocket && this.websocket.readyState === WebSocket.OPEN) {
          this.storeConversationId(this.conversationId);
        }
      }
    });
  }

  /**
   * Check for pending resume and auto-connect if valid.
   * Call this on SDK init or when widget is ready.
   * @returns {Object|null} Resume info if resuming, null otherwise
   */
  checkAndResume() {
    const resumeState = this._getResumeState();
    
    if (!resumeState) {
      return null;
    }
    
    const currentPage = window.location.pathname + window.location.search;
    
    console.log('[VoiceSDK] Auto-resuming conversation:', resumeState.conversationId);
    console.log('[VoiceSDK] Page change:', resumeState.fromPage, '→', currentPage);
    
    // Return info for caller to handle (open widget, connect, etc.)
    return {
      conversationId: resumeState.conversationId,
      fromPage: resumeState.fromPage,
      toPage: currentPage
    };
  }

  

  /**

   * Get default container based on config

   */

  getDefaultContainer(config) {

    // Backward compatibility: if user specified 'wav' as codec, use wav container

    if (config.outputCodec === 'wav') {

      return 'wav';

    }

    // Default: raw (no container overhead)

    return 'raw';

  }

  

  /**

   * Validate input audio format (matching backend validation)

   * @param {Object} format - Input format to validate

   * @returns {string|null} - Error message if invalid, null if valid

   */

  validateInputFormat(format) {

    if (!format) {

      return 'Input format is required';

    }

    

    const encoding = format.encoding?.toLowerCase();

    if (!encoding || !VoiceSDK_v2.SUPPORTED_INPUT_ENCODINGS.includes(encoding)) {

      return `Unsupported input encoding: ${format.encoding}. Supported: ${VoiceSDK_v2.SUPPORTED_INPUT_ENCODINGS.join(', ')}`;

    }

    

    if (!format.sampleRate || !VoiceSDK_v2.SUPPORTED_INPUT_SAMPLE_RATES.includes(format.sampleRate)) {

      return `Unsupported input sample rate: ${format.sampleRate}. Supported: ${VoiceSDK_v2.SUPPORTED_INPUT_SAMPLE_RATES.join(', ')}`;

    }

    

    if (!format.bitDepth || !VoiceSDK_v2.SUPPORTED_INPUT_BIT_DEPTHS.includes(format.bitDepth)) {

      return `Unsupported input bit depth: ${format.bitDepth}. Supported: ${VoiceSDK_v2.SUPPORTED_INPUT_BIT_DEPTHS.join(', ')}`;

    }

    

    if (!format.channels || !VoiceSDK_v2.SUPPORTED_INPUT_CHANNELS.includes(format.channels)) {

      return `Unsupported input channels: ${format.channels}. Supported: ${VoiceSDK_v2.SUPPORTED_INPUT_CHANNELS.join(', ')}`;

    }

    

    return null; // Valid

  }

  

  /**

   * Validate output audio format (matching backend validation)

   * @param {Object} format - Output format to validate

   * @returns {string|null} - Error message if invalid, null if valid

   */

  validateOutputFormat(format) {

    if (!format) {

      return 'Output format is required';

    }

    

    const encoding = format.encoding?.toLowerCase();

    if (!encoding || !VoiceSDK_v2.SUPPORTED_OUTPUT_ENCODINGS.includes(encoding)) {

      return `Unsupported output encoding: ${format.encoding}. Supported: ${VoiceSDK_v2.SUPPORTED_OUTPUT_ENCODINGS.join(', ')}`;

    }

    

    if (!format.sampleRate || !VoiceSDK_v2.SUPPORTED_OUTPUT_SAMPLE_RATES.includes(format.sampleRate)) {

      return `Unsupported output sample rate: ${format.sampleRate}. Supported: ${VoiceSDK_v2.SUPPORTED_OUTPUT_SAMPLE_RATES.join(', ')}`;

    }

    

    if (!format.bitDepth || !VoiceSDK_v2.SUPPORTED_OUTPUT_BIT_DEPTHS.includes(format.bitDepth)) {

      return `Unsupported output bit depth: ${format.bitDepth}. Supported: ${VoiceSDK_v2.SUPPORTED_OUTPUT_BIT_DEPTHS.join(', ')}`;

    }

    

    if (!format.channels || !VoiceSDK_v2.SUPPORTED_OUTPUT_CHANNELS.includes(format.channels)) {

      return `Unsupported output channels: ${format.channels}. Supported: ${VoiceSDK_v2.SUPPORTED_OUTPUT_CHANNELS.join(', ')}`;

    }

    

    const container = format.container?.toLowerCase();

    if (!container || !VoiceSDK_v2.SUPPORTED_OUTPUT_CONTAINERS.includes(container)) {

      return `Unsupported output container: ${format.container}. Supported: ${VoiceSDK_v2.SUPPORTED_OUTPUT_CONTAINERS.join(', ')}`;

    }

    

    return null; // Valid

  }

  

  /**

   * Validate SDK configuration and warn/error on invalid formats

   */

  validateConfig() {

    // Validate input format

    const inputFormat = {

      encoding: 'pcm', // SDK always sends PCM input

      sampleRate: this.config.sampleRate,

      channels: this.config.channels,

      bitDepth: this.config.bitDepth

    };

    

    const inputError = this.validateInputFormat(inputFormat);

    if (inputError) {

      console.error('❌ VoiceSDK v2: Invalid input format:', inputError);

      this.emit('error', new Error(inputError));

      return;

    }

    

    // Validate output format

    const outputFormat = {

      encoding: this.config.outputEncoding,

      sampleRate: this.config.outputSampleRate,

      channels: this.config.outputChannels,

      bitDepth: this.config.outputBitDepth,

      container: this.config.outputContainer

    };

    

    const outputError = this.validateOutputFormat(outputFormat);

    if (outputError) {

      console.error('❌ VoiceSDK v2: Invalid output format:', outputError);

      this.emit('error', new Error(outputError));

      return;

    }

    

    

  }

  

  /**

   * Setup AudioPlayer event listeners

   */

  setupAudioPlayerEvents() {

    this.audioPlayer.on('playbackStarted', () => {

      this.isPlaying = true;

      this.emit('playbackStarted');

      

      // Transcripts are displayed when they arrive (between sentences)
      // No need to handle transcript display here - it's handled in handleTranscript()

      

      // Notify server - CRITICAL for barge-in detection

      if (this.isConnected) {

        console.log('📤 VoiceSDK v2: Sending audio_started_playing message to server');

        this.sendMessage({ t: 'audio_started_playing' });

      } else {

        console.warn('⚠️ VoiceSDK v2: Cannot send audio_started_playing - not connected');

      }

    });
    
    // Listen for synced transcriptDisplay events from AudioPlayer
    // AudioPlayer emits these when audio actually starts playing (synced with audioContext.currentTime)
    this.audioPlayer.on('transcriptDisplay', (transcriptData) => {
      // Generate a sentenceId for tracking
      const sentenceId = this.currentPlayingSentenceId ? this.currentPlayingSentenceId + 1 : 1;
      this.currentPlayingSentenceId = sentenceId;
      
      const fullTranscriptData = {
        text: transcriptData.text,
        role: 'assistant',
        sentenceId: sentenceId,
        final: true,
        timestamp: Date.now()
      };
      
      console.log('📝 VoiceSDK v2: Emitting synced transcriptDisplay from AudioPlayer:', fullTranscriptData);
      this.emit('transcriptDisplay', fullTranscriptData);
      this.emit('transcript', fullTranscriptData);

      if (transcriptData.synced && Array.isArray(transcriptData.synced)) {
        console.log(`📝 VoiceSDK v2: Emitting ${transcriptData.synced.length} synced action(s) as message events`);
        for (const action of transcriptData.synced) {
          this.emit('message', action);
        }
      }
    });

    

    this.audioPlayer.on('playbackStopped', () => {

      this.isPlaying = false;

      this.emit('playbackStopped');

      

      // When playback stops, keep currentPlayingSentenceId to track which sentence just finished
      // The next transcript will arrive between sentences and will be displayed immediately

      

      // Notify server - CRITICAL for barge-in detection

      if (this.isConnected) {

        console.log('📤 VoiceSDK v2: Sending audio_stopped_playing message to server');

        this.sendMessage({ t: 'audio_stopped_playing' });

      } else {

        console.warn('⚠️ VoiceSDK v2: Cannot send audio_stopped_playing - not connected');

      }

    });

    

    this.audioPlayer.on('playbackError', (error) => {

      this.emit('playbackError', error);

      this.emit('error', error);

    });

  }

  

  /**

   * Setup AudioRecorder event listeners

   */

  setupAudioRecorderEvents() {

    this.audioRecorder.on('recordingStarted', () => {

      this.isRecording = true;

      

      // Barge-in detection is handled by the backend, not the frontend
      // The backend analyzes the audio stream and detects when user interrupts
      // Frontend only responds to backend 'barge_in' messages (handled in handleMessage())


      this.emit('recordingStarted');

    });

    

    this.audioRecorder.on('recordingStopped', () => {

      // Only emit if we haven't already emitted (to prevent duplicate emissions)
      // The event might already be emitted synchronously in stopRecording()
      if (this.isRecording) {
        this.isRecording = false;
        this.emit('recordingStopped');
        console.log('🛑 VoiceSDK v2: Recording stopped (from audioRecorder event)');
      }

    });

    

    this.audioRecorder.on('audioData', (pcmData) => {

      // Barge-in detection is handled by the backend, not the frontend
      // The backend analyzes the audio stream and detects when user interrupts
      // Frontend should not detect barge-in itself

      

      // Send audio chunks to server via WebSocket
      // NOTE: Don't check isRecording here - the audioRecorder emits data when it's active
      // This allows continuous frame sending even during barge-in scenarios
      // Matching v1 behavior: frames are sent as long as recorder is producing data
      if (this.isConnected) {
        if (this.isPaused) return;
        this.sendBinary(pcmData);
      }

    });

    

    this.audioRecorder.on('error', (error) => {
      // Handle server rejection gracefully (don't log or emit as error)
      if (error.isServerRejection || error.name === 'ServerRejected') {
        // Server rejection is expected - don't log or emit
        return;
      }

      console.error('❌ AudioRecorder error:', error);

      this.emit('error', error);

    });

  }

  

  /**

   * Build WebSocket URL with query parameters

   */

  /**
   * Build WebSocket URL with query parameters
   */
  buildWebSocketUrl() {
    const baseUrl = this.config.websocketUrl;
    
    const url = new URL(baseUrl, 'wss://placeholder.com');
    const params = url.searchParams;
    
    if (this.config.agentId && !params.has('agentId')) {
      params.append('agentId', this.config.agentId);
    }
    
    if (this.config.appId && !params.has('appId')) {
      params.append('appId', this.config.appId);
    }

    const storedConversationId = this.getStoredConversationId();
    if (storedConversationId && !params.has('conversationId')) {
      params.append('conversationId', storedConversationId);
      console.log('🔄 VoiceSDK v2: Attempting resume via query param:', storedConversationId);
    }

    const currentPage = window.location.pathname + window.location.search;
    if (currentPage && !params.has('currentPage')) {
      params.append('currentPage', currentPage);
    }

    const queryString = params.toString();
    if (queryString) {
      return `${url.origin}${url.pathname}?${queryString}`;
    }
    return `${url.origin}${url.pathname}`;
  }

  

  /**

   * Connect to WebSocket server

   */

  async connect() {

    if (this.isDestroyed) {

      console.warn('VoiceSDK v2: Cannot connect - SDK is destroyed');

      return false;

    }

    

    if (this.isConnected) {

      console.warn('VoiceSDK v2: Already connected');

      return true;

    }

    

    try {

      // Build WebSocket URL with query parameters (agentId and appId)

      const wsUrl = this.buildWebSocketUrl();

      if (typeof __DEV__ !== 'undefined' && __DEV__) {
        console.log('[TTPAgentSDK] WebSocket URL:', wsUrl);
        console.log('🔌 VoiceSDK v2: Connecting to', wsUrl);
      }

      

      return await new Promise((resolve, reject) => {

        this.websocket = new WebSocket(wsUrl);

        

        // Connection opened

        this.websocket.onopen = () => {

          

          this.isConnected = true;

          this.hasEverConnected = true; // Mark that we've successfully connected

          // Start keepalive pings to prevent proxy/LB idle timeout
          this._keepAliveInterval = setInterval(() => {
            if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
              this.sendMessage({ t: 'ping' });
            }
          }, 30000);

          
          // Update client tools registry sendMessage callback
          if (this.clientToolsRegistry) {
            this.clientToolsRegistry.setSendMessage((message) => this.sendMessage(message));
          }

          // Send hello message

          this.sendHelloMessage();

          

          this.emit('connected');

          resolve(true);

        };

        

        // Connection error

        this.websocket.onerror = (error) => {

          console.error('❌ VoiceSDK v2: WebSocket error:', error);

          reject(error);

        };

        

        // Connection closed

        this.websocket.onclose = (event) => {

          console.log('🔌 VoiceSDK v2: WebSocket closed:', event.code, event.reason);

          this.isConnected = false;
          this.helloAckReceived = false;
          this.disclaimersPending = false;

          // Stop keepalive pings
          if (this._keepAliveInterval) {
            clearInterval(this._keepAliveInterval);
            this._keepAliveInterval = null;
          }

          

          // Stop recording if active

          if (this.isRecording) {

            this.stopRecording().catch(err => 

              console.error('Error stopping recording:', err)

            );

          }

          // Release wake lock on disconnect
          this._releaseWakeLock();

          // Reject all pending data injections on disconnect
          if (this.pendingInjections.size > 0) {
            console.warn(`VoiceSDK v2: Rejecting ${this.pendingInjections.size} pending data injections due to disconnect`);
            const error = new Error('Connection closed - data injection cancelled');
            error.error = 'CONNECTION_CLOSED';

            this.pendingInjections.forEach((pending, correlationId) => {
              clearTimeout(pending.timeout);
              pending.reject(error);
              this.emit('dataInjectionError', {
                error: 'CONNECTION_CLOSED',
                message: 'Connection closed - data injection cancelled',
                correlationId: correlationId
              });
            });

            this.pendingInjections.clear();
          }

          this.emit('disconnected', event);

          

          // Auto-reconnect (if enabled) - only retry if we've successfully connected before

          // This prevents infinite retry loops on initial connection failures

          if (this.config.autoReconnect && !this.isDestroyed && this.hasEverConnected) {

            console.log('🔄 VoiceSDK v2: Auto-reconnecting in 3s...');

            setTimeout(() => {

              if (!this.isDestroyed && !this.isConnected) {

                this.connect();

              }

            }, 3000);

          } else if (this.config.autoReconnect && !this.hasEverConnected) {

            console.log('⚠️ VoiceSDK v2: Initial connection failed. Auto-reconnect disabled to prevent retry loop.');

          }

        };

        

        // Message received

        this.websocket.onmessage = (event) => {

          this.handleMessage(event);

        };

      });

      

    } catch (error) {

      console.error('🔌 VoiceSDK v2: Connection failed:', error);

      this.emit('error', error);

      return false;

    }

  }

  

  /**

   * Send hello message with format negotiation

   */

  /**
   * Detect browser language from navigator
   * @returns {Object} Object with language and fullLocale properties
   */
  detectBrowserLocale() {
    const fullLocale = navigator.language || navigator.languages?.[0] || 'en-US';
    const language = fullLocale.split('-')[0]?.toLowerCase() || 'en';

    return { language, fullLocale };
  }

  async sendHelloMessage() {

    if (!this.isConnected) {

      console.warn('VoiceSDK v2: Cannot send hello - not connected');

      return;

    }

    

    // Build input format object

    const inputFormat = {

      encoding: 'pcm', // "pcm", "pcmu", "pcma"

      sampleRate: this.config.sampleRate, // Integer

      channels: this.config.channels, // Integer

      bitDepth: this.config.bitDepth // Integer

    };

    

    // Build requested output format object

    const requestedOutputFormat = {

      encoding: this.config.outputEncoding, // "pcm", "pcmu", "pcma"

      sampleRate: this.config.outputSampleRate, // Integer

      channels: this.config.outputChannels, // Integer

      bitDepth: this.config.outputBitDepth, // Integer

      container: this.config.outputContainer // "raw" or "wav" (for output format only)

    };

    

    // Validate formats before sending (matching backend validation)

    const inputError = this.validateInputFormat(inputFormat);

    if (inputError) {

      console.error('❌ VoiceSDK v2: Cannot send hello - invalid input format:', inputError);

      this.emit('error', new Error(`Invalid input format: ${inputError}`));

      return;

    }

    

    const outputError = this.validateOutputFormat(requestedOutputFormat);

    if (outputError) {

      console.error('❌ VoiceSDK v2: Cannot send hello - invalid output format:', outputError);

      this.emit('error', new Error(`Invalid output format: ${outputError}`));

      return;

    }

    

    // Build hello message matching backend ControlMessage structure

    const helloMessage = {

      t: 'hello',

      v: this.config.protocolVersion, // Protocol version (Integer)

      inputFormat: inputFormat,

      requestedOutputFormat: requestedOutputFormat,

      outputFrameDurationMs: this.config.outputFrameDurationMs // Frame duration in milliseconds

    };

    // Optional: Agent settings override (Map<String, Object>)

    if (this.config.agentSettingsOverride) {

      helloMessage.agentSettingsOverride = this.config.agentSettingsOverride;

    }

    

    // Automatically detect browser language
    helloMessage.locale = this.detectBrowserLocale();
    console.log('🌍 VoiceSDK v2: Auto-detected locale:', helloMessage.locale);

    // Optional: Variables (Map<String, String>)
    console.log('🔍 VoiceSDK v2: Checking variables:', {
      hasVariables: !!this.config.variables,
      variables: this.config.variables,
      keys: this.config.variables ? Object.keys(this.config.variables) : []
    });

    if (this.config.variables && Object.keys(this.config.variables).length > 0) {

      helloMessage.variables = this.config.variables;

      console.log('📝 VoiceSDK v2: Including variables in hello:', Object.keys(this.config.variables));

    } else {

      console.log('⚠️ VoiceSDK v2: Variables NOT included - condition failed');

    }


    // Include flavor object if set (additive — backward compatible)
    if (this.config.flavor) {
      helloMessage.flavor = this.config.flavor;
    }

    // Include SDK build time for debugging
    if (typeof __BUILD_TIME__ !== 'undefined') {
      helloMessage.lastBuildTime = __BUILD_TIME__;
    }

    // Page context is intentionally NOT attached to the hello message.
    // The backend caches the system prompt across turns (Anthropic prompt
    // caching), so anything that varies per-page would invalidate the
    // cache. The backend pulls page context on demand via the
    // `get_page_context` control message, handled below.

    // Note: appId and agentId are sent as query parameters in WebSocket URL, not in hello message

    // (matching v1 behavior and backend expectations)

    

    console.log('📤 VoiceSDK v2: Sending hello:', {

      version: helloMessage.v,

      requestedFormat: helloMessage.requestedOutputFormat,

      variables: helloMessage.variables ? Object.keys(helloMessage.variables) : undefined

    });

    console.log('📤 VoiceSDK v2: Requested output format:', {

      container: helloMessage.requestedOutputFormat.container,

      encoding: helloMessage.requestedOutputFormat.encoding,

      sampleRate: helloMessage.requestedOutputFormat.sampleRate,

      bitDepth: helloMessage.requestedOutputFormat.bitDepth

    });

    
    // Debug: Log the full hello message before sending
    console.log('📤 VoiceSDK v2: Full hello message being sent:', JSON.stringify(helloMessage, null, 2));

    try {

      this.sendMessage(helloMessage);

    } catch (error) {

      console.error('VoiceSDK v2: Failed to send hello:', error);

      this.emit('error', error);

    }

  }

  

  /**

   * Handle incoming WebSocket message

   */

  handleMessage(event) {

    // Binary message (audio data)

    if (event.data instanceof ArrayBuffer || event.data instanceof Blob) {

      this.handleBinaryMessage(event.data);

      return;

    }

    

    // Text message (JSON)

    try {

      const message = JSON.parse(event.data);

      

      // Log all non-audio messages for debugging transcript issues
      if (message.t !== 'audio') {
        console.log('📥 VoiceSDK v2: Received message type:', message.t, 'Full message:', JSON.stringify(message).substring(0, 200));
      }

      

      // Fallback: capture conversationId if present on any message (if not already set)
      if (!this.conversationId && message.conversationId) {
        this.conversationId = message.conversationId;
        console.log('🔍 VoiceSDK v2: Captured conversationId from message:', this.conversationId);
        this.emit('conversationIdChanged', this.conversationId);
      }

      // Log all received messages for debugging
      if (message.t !== 'audio') { // Don't log audio messages (too verbose)
        console.log('📨 VoiceSDK v2: Received message type:', message.t, message);
      }

      switch (message.t) {

        case 'hello_ack':

          this.handleHelloAck(message);

          break;

        case 'transcript':
          // Handle transcript messages - store by sentenceId
          console.log('📝 VoiceSDK v2: Processing transcript message:', message);
          this.handleTranscript(message);
          break;

        case 'user_transcript':
          console.log('📝 VoiceSDK v2: Received user_transcript:', message);
          this.emit('user_transcript', {
            text: message.text,
            final: message.final !== false,
            timestamp: message.timestamp || Date.now()
          });
          break;

        case 'agent_response':
          console.log('📝 VoiceSDK v2: Received agent_response:', message);
          // Check if agent_response contains transcript data
          if (message.text || message.transcript) {
            console.log('📝 VoiceSDK v2: agent_response contains transcript data:', message.text || message.transcript);
            // Try to handle as transcript if it has the right structure
            if (message.sentenceId && (message.text || message.transcript)) {
              const transcriptMessage = {
                t: 'transcript',
                sentenceId: message.sentenceId,
                text: message.text || message.transcript,
                role: message.role || 'assistant',
                final: message.final !== false,
                timestamp: message.timestamp || Date.now()
              };
              console.log('📝 VoiceSDK v2: Converting agent_response to transcript:', transcriptMessage);
              this.handleTranscript(transcriptMessage);
            }
          }
          this.emit('message', message);
          break;

        case 'error':
          if (message.code && String(message.code).startsWith('DISCLAIMER_')) {
            if (message.code === 'DISCLAIMER_PENDING') {
              const err = new Error(message.message || 'Disclaimer acknowledgement required first');
              err.code = 'DISCLAIMER_PENDING';
              this.emit('error', err);
            } else {
              this.disclaimersPending = false;
              this.emit('disclaimerRejected', { code: message.code, message: message.message || '' });
            }
          }
          this.emit('message', message);

          break;

        

        case 'barge_in':

          this.emit('bargeIn', message);

          this.stopAudioPlayback();

          break;

        

        case 'stop_playing':
          // CRITICAL: Ignore stop_playing messages that arrive too soon after audio_start
          // Backend sometimes sends stop_playing immediately after audio_start, which cuts sentences prematurely
          // Only honor stop_playing if it's been at least 200ms since the last audio_start
          const timeSinceAudioStart = Date.now() - this.lastAudioStartTime;
          const MIN_STOP_PLAYING_DELAY_MS = 200; // 200ms grace period after audio_start
          
          if (timeSinceAudioStart < MIN_STOP_PLAYING_DELAY_MS) {
            console.warn(`⚠️ VoiceSDK v2: Ignoring premature stop_playing (${timeSinceAudioStart}ms after audio_start, minimum ${MIN_STOP_PLAYING_DELAY_MS}ms required)`);
            break;
          }

          this.emit('stopPlaying', message);

          this.stopAudioPlayback();

          break;

        case 'audio_start':
          // Backend sends audio_start with transcript text before audio chunks
          // Store the text in AudioPlayer for synced display when audio actually starts playing
          console.log('📝 VoiceSDK v2: Received audio_start with text:', message.text);
          
          // CRITICAL: Record timestamp to prevent premature stop_playing messages from cutting sentences
          this.lastAudioStartTime = Date.now();
          
          // NOTE: We do NOT stop current audio here - that only happens on user barge-in (stop_playing)
          // If audio is already playing, the new sentence will queue and wait for current one to finish
          // This allows sentences to play sequentially without interruption
          if (this.audioPlayer && (this.audioPlayer.isPlaying || this.audioPlayer.scheduledSources?.size > 0)) {
            console.log('📝 VoiceSDK v2: New sentence received while audio playing - will queue and wait for current sentence to finish');
          }
          
          if (message.text && this.audioPlayer) {
            const synced = Array.isArray(message.synced) ? message.synced : null;
            if (synced) {
              console.log(`📝 VoiceSDK v2: audio_start carries ${synced.length} synced action(s)`);
            }
            this.audioPlayer.markNewSentence(message.text, synced);
          }
          // Also emit as message for other listeners
          this.emit('message', message);
          break;

        case 'client_tool_call':
          // Partner-script dispatch for the sentinel __client_tools__ partner id
          // rides on client_tool_call (toolName 'run_partner_script'). Route it
          // to the flavor-independent ClientScriptManager — with no flavor
          // loaded there is no registered 'run_partner_script' handler and the
          // registry would NO_HANDLER it. Every other tool call (including
          // partner-id'd run_partner_script for the ecommerce flavor) keeps
          // going through the registry untouched.
          if (ClientScriptManager.isClientToolsToolCall(message)) {
            this.clientScriptManager.handleClientToolCall(message, this.conversationId);
            break;
          }
          this.clientToolsRegistry.handleToolCall(message);
          break;

        case 'run_partner_script':
          // Asynchronous partner-adapter dispatch — separate from client_tool_call.
          // No toolCallId, never closes a pending LLM tool_call. Backend has
          // already returned a "clientSide<X>:true" placeholder synchronously
          // and registered a callback keyed by requestId. We invoke the same
          // 'run_partner_script' handler (registered by the ecommerce flavor),
          // then post the outcome back as a separate run_partner_script_result
          // envelope which the backend dispatcher routes to the callback.
          this._handleRunPartnerScript(message);
          break;

        case 'partner_bundle':
          // Flavor-independent client-tool bundle (partner_id `__client_tools__`)
          // is loaded by the dedicated ClientScriptManager. Every other partner
          // id falls through to the flavor (via the emitted 'message' event), so
          // the ecommerce flavor's own bundle handling keeps working untouched.
          if (ClientScriptManager.isClientToolsBundle(message)) {
            this.clientScriptManager.setBundle(message);
          }
          this.emit('message', message);
          break;

        case 'text_inject_ack':
          this.handleTextInjectAck(message);
          break;

        case 'data_inject_ack':
          this.handleDataInjectAck(message);
          break;

        case 'data_inject_error':
          this.handleDataInjectError(message);
          break;

        case 'hangup_ack':
          console.log('📞 VoiceSDK v2: Hangup acknowledged by server');
          // Backend acknowledged hangup, close WebSocket gracefully
          if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.close(1000, 'User hangup');
            this.websocket = null;
          }
          this.isConnected = false;
          this.emit('hangupAcknowledged');
          break;

        case 'get_page_context':
          // Handle get_page_context request from backend
          console.log('📖 [VISUAL ASSISTANT] Backend requested page context');
          
          extractPageContext().then(pageContext => {
            // Use existing DOM scanner
            this.sendMessage({
              t: 'page_context',
              pageContext: pageContext
            });
            
            console.log('📖 [VISUAL ASSISTANT] Page context sent:', pageContext.url);
          }).catch(error => {
            console.error('❌ [VISUAL ASSISTANT] Failed to scan page:', error);
            this.sendMessage({
              t: 'page_context',
              pageContext: null,
              error: error.message
            });
          });
          break;

        case 'get_screenshot':
          // Handle get_screenshot request from backend
          console.log('📸 [VISUAL ASSISTANT] Backend requested screenshot');
          
          try {
            // Use existing screenshot capture function
            captureScreenshot().then(screenshot => {
              this.sendMessage({
                t: 'screenshot',
                screenshot: {
                  image: screenshot.base64,
                  mimeType: screenshot.mimeType || 'image/png',
                  width: screenshot.width,
                  height: screenshot.height
                }
              });
              
              console.log('📸 [VISUAL ASSISTANT] Screenshot sent:', screenshot.width + 'x' + screenshot.height);
            }).catch(error => {
              console.error('❌ [VISUAL ASSISTANT] Screenshot capture failed:', error);
              this.sendMessage({
                t: 'screenshot',
                screenshot: null,
                error: error.message
              });
            });
          } catch (error) {
            console.error('❌ [VISUAL ASSISTANT] Screenshot error:', error);
            this.sendMessage({
              t: 'screenshot',
              screenshot: null,
              error: error.message
            });
          }
          break;

        case 'page_context_ack':
          // Handle page context acknowledgment
          if (message.ok) {
            console.log('✅ [VISUAL ASSISTANT] Page context acknowledged');
          } else {
            console.warn('⚠️ [VISUAL ASSISTANT] Page context error:', message.error);
          }
          break;

        case 'screenshot_ack':
          // Handle screenshot acknowledgment
          if (message.ok) {
            console.log('✅ [VISUAL ASSISTANT] Screenshot acknowledged');
          } else {
            console.warn('⚠️ [VISUAL ASSISTANT] Screenshot error:', message.error);
          }
          break;

        case 'show_items':
        case 'show_products':
        case 'cart_updated':
        case 'add_to_store_cart':
        case 'add_to_site_cart':
        case 'get_site_cart':
        case 'simulate_search_on_site':
        case 'site_action':
          // Flavor / commerce messages (show_items = menu, rooms, tours, etc.)
          this.emit('message', message);
          break;

        case 'call_paused':
          this.isPaused = true;
          this.emit('callPaused', message);
          break;

        case 'call_resumed':
          this.isPaused = false;
          this.startRecording();
          this.emit('callResumed', message);
          break;

        case 'pause_timeout':
          this.emit('pauseTimeout', message);
          this.hangup();
          break;

        default:
          console.log('VoiceSDK v2: Unknown message type:', message.t, 'Full message:', JSON.stringify(message).substring(0, 300));
          // Check if unknown message contains transcript data
          if (message.text || message.transcript || message.transcription) {
            console.log('📝 VoiceSDK v2: Unknown message contains transcript-like data:', {
              text: message.text,
              transcript: message.transcript,
              transcription: message.transcription,
              sentenceId: message.sentenceId,
              role: message.role
            });
            // Try to handle as transcript if it has the right structure
            if (message.sentenceId && (message.text || message.transcript || message.transcription)) {
              const transcriptMessage = {
                t: 'transcript',
                sentenceId: message.sentenceId,
                text: message.text || message.transcript || message.transcription,
                role: message.role || 'assistant',
                final: message.final !== false,
                timestamp: message.timestamp || Date.now()
              };
              console.log('📝 VoiceSDK v2: Attempting to process unknown message as transcript:', transcriptMessage);
              this.handleTranscript(transcriptMessage);
            }
          }
          this.emit('message', message);

      }

      

    } catch (error) {

      // Enhanced error logging to help debug JSON parsing issues
      const rawData = typeof event.data === 'string' ? event.data : String(event.data);
      const errorPosition = error.message?.match(/position (\d+)/)?.[1] || 'unknown';
      const position = parseInt(errorPosition, 10);
      
      console.error('VoiceSDK v2: Error parsing message:', error);
      console.error('VoiceSDK v2: Raw message length:', rawData.length);
      console.error('VoiceSDK v2: Error at position:', position);
      
      // Show context around the error position
      if (position !== 'unknown' && position > 0) {
        const start = Math.max(0, position - 50);
        const end = Math.min(rawData.length, position + 50);
        const context = rawData.substring(start, end);
        const relativePos = position - start;
        const marker = ' '.repeat(relativePos) + '^';
        console.error('VoiceSDK v2: Context around error:');
        console.error(context);
        console.error(marker);
        
        // Show the problematic character(s)
        const problematicChar = rawData.charAt(position);
        const charCode = problematicChar.charCodeAt(0);
        console.error(`VoiceSDK v2: Problematic character: "${problematicChar}" (charCode: ${charCode})`);
        
        // Check for common control characters
        if (charCode < 32 && charCode !== 9 && charCode !== 10 && charCode !== 13) {
          console.error('VoiceSDK v2: This is an unescaped control character. The backend should escape it in JSON strings.');
        }
      }
      
      // Try to detect if this was a client_tool_call message
      const looksLikeToolCall = rawData.includes('client_tool_call') || rawData.includes('"t":"client_tool_call"');
      if (looksLikeToolCall) {
        console.error('⚠️ VoiceSDK v2: CRITICAL - Failed to parse client_tool_call message!');
        console.error('⚠️ VoiceSDK v2: This means a tool execution (like form filling) was lost due to JSON parsing error.');
        console.error('⚠️ VoiceSDK v2: The backend sent malformed JSON with unescaped control characters.');
        
        // Try to extract tool name from raw string (basic regex attempt)
        const toolNameMatch = rawData.match(/"toolName"\s*:\s*"([^"]+)"/);
        if (toolNameMatch) {
          console.error(`⚠️ VoiceSDK v2: Attempted tool: "${toolNameMatch[1]}"`);
        }
        
        // Try to extract toolCallId
        const toolCallIdMatch = rawData.match(/"toolCallId"\s*:\s*"([^"]+)"/);
        if (toolCallIdMatch) {
          console.error(`⚠️ VoiceSDK v2: Tool call ID: "${toolCallIdMatch[1]}"`);
        }
      }
      
      // Attempt recovery: sanitize control characters and try parsing again
      if (error.message?.includes('Bad control character')) {
        console.warn('VoiceSDK v2: Attempting to recover by sanitizing control characters...');
        try {
          // Replace unescaped control characters (except \t, \n, \r which are sometimes valid)
          // This is a best-effort recovery - may not work for all cases
          let sanitized = rawData;
          // Replace control characters that aren't properly escaped
          // Look for control chars that aren't part of \n, \t, \r, \\ sequences
          sanitized = sanitized.replace(/([^\\])([\x00-\x08\x0B\x0C\x0E-\x1F])/g, (match, prev, ctrl) => {
            const code = ctrl.charCodeAt(0);
            // Map common control chars to escape sequences
            const escapes = {
              0: '\\u0000', 1: '\\u0001', 2: '\\u0002', 3: '\\u0003',
              4: '\\u0004', 5: '\\u0005', 6: '\\u0006', 7: '\\u0007',
              8: '\\b', 11: '\\u000B', 12: '\\f',
              14: '\\u000E', 15: '\\u000F', 16: '\\u0010',
              17: '\\u0011', 18: '\\u0012', 19: '\\u0013',
              20: '\\u0014', 21: '\\u0015', 22: '\\u0016', 23: '\\u0017',
              24: '\\u0018', 25: '\\u0019', 26: '\\u001A', 27: '\\u001B',
              28: '\\u001C', 29: '\\u001D', 30: '\\u001E', 31: '\\u001F'
            };
            return prev + (escapes[code] || `\\u${code.toString(16).padStart(4, '0')}`);
          });
          
          const recoveredMessage = JSON.parse(sanitized);
          console.warn('✅ VoiceSDK v2: Successfully recovered message after sanitization!');
          console.warn('✅ VoiceSDK v2: Processing recovered message...');
          
          // Process the recovered message
          if (recoveredMessage.t === 'client_tool_call' && this.clientToolsRegistry) {
            console.warn('✅ VoiceSDK v2: Recovered client_tool_call - executing tool');
            this.clientToolsRegistry.handleToolCall(recoveredMessage);
          } else {
            // Re-process through normal flow
            const message = recoveredMessage;
            if (message.t !== 'audio') {
              console.log('📥 VoiceSDK v2: Received message type:', message.t, 'Full message:', JSON.stringify(message).substring(0, 200));
            }
            if (!this.conversationId && message.conversationId) {
              this.conversationId = message.conversationId;
              console.log('🔍 VoiceSDK v2: Captured conversationId from message:', this.conversationId);
              this.emit('conversationIdChanged', this.conversationId);
            }
            if (message.t !== 'audio') {
              console.log('📨 VoiceSDK v2: Received message type:', message.t, message);
            }
            // Re-enter switch statement logic (simplified - just handle client_tool_call)
            if (message.t === 'client_tool_call') {
              this.clientToolsRegistry.handleToolCall(message);
            } else {
              this.emit('message', message);
            }
          }
          return; // Successfully recovered, exit error handler
        } catch (recoveryError) {
          console.error('❌ VoiceSDK v2: Recovery attempt failed:', recoveryError);
        }
      }
      
      // Show first 500 chars of raw message for debugging
      console.error('VoiceSDK v2: First 500 chars of raw message:', rawData.substring(0, 500));
      
      // Emit error event so listeners can handle it
      this.emit('error', {
        type: 'json_parse_error',
        error: error,
        rawMessage: rawData.substring(0, 1000), // Include first 1000 chars for debugging
        position: position,
        wasToolCall: looksLikeToolCall
      });

    }

  }

  

  /**

   * Handle hello_ack message

   */

  handleHelloAck(message) {

    console.log('📥 VoiceSDK v2: Received hello_ack:', {
      hasOutputFormat: !!message.outputAudioFormat,
      outputFormat: message.outputAudioFormat,
      hasConversationId: !!message.conversationId,
      messageType: message.t
    });

    // Capture conversation ID if provided
    if (message.conversationId) {
      this.conversationId = message.conversationId;
      console.log('🔍 VoiceSDK v2: Received conversationId:', this.conversationId);
      this.emit('conversationIdChanged', this.conversationId);
      
      // Store conversationId (also updates resume state with timestamp)
      this.storeConversationId(this.conversationId);
    }

    // Handle resume response
    if (message.resumed === true) {
      console.log('✅ VoiceSDK v2: Conversation resumed successfully');
      this.emit('callResumed', {
        conversationId: this.conversationId,
        timestamp: Date.now()
      });
    } else if (message.resumed === false && message.resumeRejectedReason) {
      console.warn('⚠️ VoiceSDK v2: Resume rejected:', message.resumeRejectedReason);
      // Clear invalid conversationId
      this.clearStoredConversationId();
      this.emit('resumeRejected', {
        reason: message.resumeRejectedReason,
        conversationId: message.conversationId || null,
        timestamp: Date.now()
      });
    }

    // Negotiate input audio format from server (STT provider dictates sample rate)
    if (message.inputAudioFormat) {
      const serverRate = message.inputAudioFormat.sampleRate;
      if (serverRate && serverRate !== this.config.sampleRate) {
        console.log(`🔄 VoiceSDK v2: Input format negotiated by server: ${this.config.sampleRate}Hz → ${serverRate}Hz`);
        this.config.sampleRate = serverRate;
      }
      if (message.inputAudioFormat.channels) this.config.channels = message.inputAudioFormat.channels;
      if (message.inputAudioFormat.bitDepth) this.config.bitDepth = message.inputAudioFormat.bitDepth;

      // AudioRecorder holds a shallow copy of config from construction time —
      // update it so it creates the AudioContext at the negotiated rate
      if (this.audioRecorder && this.audioRecorder.config) {
        this.audioRecorder.config.sampleRate = this.config.sampleRate;
        this.audioRecorder.config.channels = this.config.channels;
        this.audioRecorder.config.bitDepth = this.config.bitDepth;
      }

      this.emit('inputFormatNegotiated', message.inputAudioFormat);
    }

    // Server-driven disclaimer gate (voice): when true, client must send disclaimer_ack before using the session.
    this.disclaimersPending = message.disclaimersRequired === true;
    this.disclaimersHash = this.disclaimersPending ? (message.disclaimersHash || null) : null;

    // IMPORTANT: Set this AFTER input format negotiation above.
    // VoiceInterface waits for this flag before starting recording,
    // so config.sampleRate must be finalized first.
    this.helloAckReceived = true;

    if (message.outputAudioFormat) {

      // v2 protocol: format was negotiated

      this.outputAudioFormat = message.outputAudioFormat;

      this.audioPlayer.setOutputFormat(message.outputAudioFormat);

      

      console.log('✅ VoiceSDK v2: Format negotiated by server:', this.outputAudioFormat);
      
      // Store requested format
      this.requestedOutputFormat = {
        container: this.config.outputContainer || 'wav',
        encoding: this.config.outputEncoding || 'pcm',
        sampleRate: this.config.outputSampleRate || 24000,
        bitDepth: this.config.outputBitDepth || 16,
        channels: this.config.outputChannels || 1
      };
      
      const negotiatedFormat = {
        container: this.outputAudioFormat.container || 'unknown',
        encoding: this.outputAudioFormat.encoding || 'unknown',
        sampleRate: this.outputAudioFormat.sampleRate || 0,
        bitDepth: this.outputAudioFormat.bitDepth || 0,
        channels: this.outputAudioFormat.channels || 0
      };
      
      // Check for mismatches and create converter if needed
      const mismatches = [];
      if (this.requestedOutputFormat.container !== negotiatedFormat.container) {
        mismatches.push(`container: "${this.requestedOutputFormat.container}" → "${negotiatedFormat.container}"`);
      }
      if (this.requestedOutputFormat.encoding !== negotiatedFormat.encoding) {
        mismatches.push(`encoding: "${this.requestedOutputFormat.encoding}" → "${negotiatedFormat.encoding}"`);
      }
      if (this.requestedOutputFormat.sampleRate !== negotiatedFormat.sampleRate) {
        mismatches.push(`sampleRate: ${this.requestedOutputFormat.sampleRate}Hz → ${negotiatedFormat.sampleRate}Hz`);
      }
      if (this.requestedOutputFormat.bitDepth !== negotiatedFormat.bitDepth) {
        mismatches.push(`bitDepth: ${this.requestedOutputFormat.bitDepth}-bit → ${negotiatedFormat.bitDepth}-bit`);
      }
      if (this.requestedOutputFormat.channels !== negotiatedFormat.channels) {
        mismatches.push(`channels: ${this.requestedOutputFormat.channels} → ${negotiatedFormat.channels}`);
      }
      
      if (mismatches.length > 0) {
        console.warn('⚠️ VoiceSDK v2: Format negotiation mismatch!');
        console.warn('   Requested:', this.requestedOutputFormat);
        console.warn('   Negotiated:', negotiatedFormat);
        console.warn('   Differences:', mismatches.join(', '));
        
        // Create format converter to automatically convert
        try {
          this.formatConverter = new AudioFormatConverter(
            this.requestedOutputFormat,
            negotiatedFormat
          );
          
          if (this.formatConverter.needsConversion()) {
            const steps = this.formatConverter.getConversionSteps();
            console.log('🔄 VoiceSDK v2: Format conversion enabled');
            console.log('   Conversion steps:', steps.join(', '));
            console.log('   Audio will be automatically converted to requested format');
          }
        } catch (error) {
          console.error('❌ VoiceSDK v2: Failed to create format converter:', error);
          console.warn('   Will use backend format without conversion');
          this.formatConverter = null;
        }
      } else {
        console.log('✅ VoiceSDK v2: Format perfectly matched!', negotiatedFormat);
        this.formatConverter = null; // No conversion needed
      }

      this.emit('formatNegotiated', this.outputAudioFormat);

      

    } else {

      // v2 protocol but backend didn't include outputAudioFormat
      // Fallback to default format

      if (this.config.protocolVersion >= 2) {

        console.warn('⚠️ VoiceSDK v2: hello_ack received but no outputAudioFormat in response.');

        console.warn('   This may happen if:');

        console.warn('   1. Backend is not sending outputAudioFormat (check backend logs)');

        console.warn('   2. Hello message missing v2 fields (v, inputFormat, requestedOutputFormat)');

        console.warn('   3. Backend validation failed (check backend error logs)');

        console.warn('   Using default format from config...');

      }

      

      // Use defaults from config (already set during initialization, but update if needed)

      const defaultFormat = {

        container: this.config.outputContainer || 'wav',

        encoding: this.config.outputEncoding || 'pcm',

        sampleRate: this.config.outputSampleRate || 24000,

        channels: this.config.outputChannels || 1,

        bitDepth: this.config.outputBitDepth || 16

      };

      

      this.outputAudioFormat = defaultFormat;
      
      this.requestedOutputFormat = defaultFormat; // Same as default when no negotiation

      this.audioPlayer.setOutputFormat(defaultFormat);
      
      this.formatConverter = null; // No conversion needed when using defaults

    }

    if (this.disclaimersPending) {
      const payload = {
        texts: Array.isArray(message.disclaimerTexts) ? message.disclaimerTexts : [],
        disclaimersHash: this.disclaimersHash,
        disclaimersVersion: message.disclaimersVersion,
        disclaimerTimeoutMs: message.disclaimerTimeoutMs,
        conversationId: this.conversationId
      };
      this.lastDisclaimerPayload = payload;
      this.emit('disclaimersRequired', payload);
    } else {
      this.lastDisclaimerPayload = null;
    }

  }

  /**
   * Acknowledge server disclaimer gate (voice protocol).
   * @param {boolean} accepted
   */
  sendDisclaimerAck(accepted) {
    if (!this.isConnected || !this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.warn('VoiceSDK v2: Cannot send disclaimer_ack — socket not open');
      return;
    }
    const payload = {
      t: 'disclaimer_ack',
      accepted: !!accepted,
      disclaimersHash: this.disclaimersHash || '',
      conversationId: this.conversationId || undefined
    };
    try {
      this.websocket.send(JSON.stringify(payload));
      console.log('📤 VoiceSDK v2: disclaimer_ack sent', { accepted: payload.accepted });
      this.disclaimersPending = false;
    } catch (e) {
      console.error('VoiceSDK v2: disclaimer_ack send failed', e);
    }
  }

  

  /**
   * Handle a run_partner_script envelope (async partner-adapter dispatch).
   *
   * Wire protocol:
   *   backend → widget : { t:'run_partner_script', requestId, partnerId, action, args }
   *   widget  → backend: { t:'run_partner_script_result', requestId, partnerId, action, ok, result|error }
   *
   * Reuses the registered 'run_partner_script' tool handler (the one wired up
   * by the ecommerce flavor via buildRunPartnerScriptTool) so adapter
   * compilation / execution / ctx-building stay in one place. We just wrap the
   * dispatch in the async envelope on either side.
   *
   * @param {Object} message - { t, requestId, partnerId, action, args }
   * @private
   */
  async _handleRunPartnerScript(message) {
    const { requestId, partnerId, action } = message || {};
    const args = (message && message.args) || {};

    if (!requestId) {
      console.warn('⚠️ VoiceSDK v2: run_partner_script missing requestId; dropping', message);
      return;
    }

    // Route by partner id. The reserved `__client_tools__` partner is owned by
    // the flavor-independent ClientScriptManager (its own runner + 50KB cap +
    // result envelope). Every other partner id resolves the single
    // 'run_partner_script' tool handler registered by the ecommerce flavor.
    if (ClientScriptManager.isClientToolsScript(message)) {
      return this.clientScriptManager.handleRunPartnerScript(message);
    }

    const handler = this.clientToolsRegistry?.handlers?.get('run_partner_script');
    if (!handler) {
      console.error(`🧩 VoiceSDK v2: no run_partner_script handler registered (ecommerce flavor not loaded?); requestId=${requestId} partner=${partnerId} action=${action}`);
      this.sendMessage({
        t: 'run_partner_script_result',
        requestId,
        partnerId,
        action,
        ok: false,
        error: 'no_handler_registered',
      });
      return;
    }

    const startedAt = Date.now();
    console.log(`🧩 VoiceSDK v2: run_partner_script dispatch partner=${partnerId} action=${action} requestId=${requestId}`);

    try {
      const result = await handler({ action, args });
      const elapsedMs = Date.now() - startedAt;
      // Adapters return { ok, ... } themselves. Honor that flag if present;
      // otherwise treat presence-of-result as success.
      const ok = result && typeof result === 'object' && 'ok' in result
        ? !!result.ok
        : true;
      // Surface the failure reason inline so debugging doesn't require pulling
      // backend logs. Adapters return { ok:false, reason:'...', ... } by
      // convention — log the reason + a short payload preview when ok=false.
      if (ok) {
        console.log(`🧩 VoiceSDK v2: run_partner_script result partner=${partnerId} action=${action} requestId=${requestId} ok=true (${elapsedMs}ms)`);
      } else {
        const reason = (result && (result.reason || result.error)) || 'unknown';
        const extras = result && typeof result === 'object'
          ? Object.keys(result).filter((k) => k !== 'ok' && k !== 'reason' && k !== 'error').slice(0, 6)
          : [];
        const preview = extras.length
          ? ' fields=[' + extras.map((k) => `${k}=${JSON.stringify(result[k])?.slice(0, 80)}`).join(', ') + ']'
          : '';
        console.warn(`🧩 VoiceSDK v2: run_partner_script result partner=${partnerId} action=${action} requestId=${requestId} ok=false reason=${reason} (${elapsedMs}ms)${preview}`, result);
      }
      this.sendMessage({
        t: 'run_partner_script_result',
        requestId,
        partnerId,
        action,
        ok,
        result,
      });
    } catch (err) {
      const elapsedMs = Date.now() - startedAt;
      console.error(`🧩 VoiceSDK v2: run_partner_script threw partner=${partnerId} action=${action} requestId=${requestId} (${elapsedMs}ms):`, err);
      this.sendMessage({
        t: 'run_partner_script_result',
        requestId,
        partnerId,
        action,
        ok: false,
        error: (err && err.message) || String(err),
      });
    }
  }

  /**

   * Handle acknowledgment for text injection.

   * @param {Object} message - The ack message data

   */

  handleTextInjectAck(message) {

    console.log('📝 VoiceSDK v2: Text inject ack received:', message);

    

    // Emit event for VoiceInterface to handle

    this.emit('textInjectAck', message);

  }

  /**
   * Inject data/text into the active voice conversation programmatically.
   * This uses a new message type called data_inject, separate from the existing text_inject.
   * 
   * @param {string} data - The data to inject (non-empty, max 5000 chars)
   * @param {Object} options - Optional configuration
   * @param {string} options.source - Source identifier (default: 'programmatic')
   * @param {Object} options.metadata - Optional metadata (will be echoed back in ack)
   * @param {number} options.timeout - Timeout in milliseconds (default: 10000)
   * @returns {Promise<Object>} Promise resolving to acknowledgment message on success
   * @throws {Error} If validation fails (empty data, not connected, data too long) or timeout occurs
   * 
   * @fires VoiceSDK#dataInjected - When injection is successfully acknowledged by server
   * @fires VoiceSDK#dataInjectionError - When injection fails (validation error, timeout, or server error)
   * 
   * @example
   * const ack = await voiceSDK.injectData('User selected product: iPhone 15', {
   *   source: 'product_page',
   *   metadata: { productId: 'iphone-15', price: 999 },
   *   timeout: 5000
   * });
   */
  async injectData(data, options = {}) {
    // Validation: data content
    const trimmedData = data?.trim();
    if (!trimmedData) {
      const error = {
        error: 'EMPTY_DATA',
        message: 'Cannot inject empty data'
      };
      console.warn('VoiceSDK v2: Cannot inject empty data');
      this.emit('dataInjectionError', error);
      throw new Error(error.message);
    }

    // Validation: connection check
    if (!this.isConnected || !this.websocket) {
      const error = {
        error: 'NOT_CONNECTED',
        message: 'Voice call is not active'
      };
      console.warn('VoiceSDK v2: Cannot inject data - not connected');
      this.emit('dataInjectionError', error);
      throw new Error(error.message);
    }

    // Validation: data length (max 5000 chars)
    if (trimmedData.length > 5000) {
      const error = {
        error: 'DATA_TOO_LONG',
        message: 'Data exceeds maximum length of 5000 characters',
        maxLength: 5000,
        currentLength: trimmedData.length
      };
      console.warn('VoiceSDK v2: Data too long (max 5000 characters)');
      this.emit('dataInjectionError', error);
      throw new Error(error.message);
    }

    // Generate correlation ID for this injection
    const correlationId = this.generateCorrelationId();
    const timeout = options.timeout || 10000;
    const source = options.source || 'programmatic';

    // Build message (data_inject type, separate from text_inject)
    const message = {
      t: 'data_inject',
      data: trimmedData,
      correlationId: correlationId,
      source: source,
      timestamp: Date.now()
    };

    // Add optional metadata if provided (will be echoed back in ack)
    if (options.metadata) {
      message.metadata = options.metadata;
    }

    // Return Promise that resolves when acknowledgment is received
    return new Promise((resolve, reject) => {
      // Set up timeout
      const timeoutId = setTimeout(() => {
        // Remove from pending map
        this.pendingInjections.delete(correlationId);

        const error = {
          error: 'TIMEOUT',
          message: 'Data injection timeout - no response from server',
          correlationId: correlationId,
          timeout: timeout
        };
        console.warn('VoiceSDK v2: Data injection timeout:', correlationId);
        this.emit('dataInjectionError', error);
        reject(new Error(error.message));
      }, timeout);

      // Store Promise handlers BEFORE sending message (prevents race conditions)
      this.pendingInjections.set(correlationId, {
        resolve: (ackMessage) => {
          clearTimeout(timeoutId);
          resolve(ackMessage);
        },
        reject: (error) => {
          clearTimeout(timeoutId);
          reject(error);
        },
        timeout: timeoutId
      });

      // Send message
      const sent = this.sendMessage(message);
      if (!sent) {
        // Failed to send - clean up immediately
        this.pendingInjections.delete(correlationId);
        clearTimeout(timeoutId);
        const error = {
          error: 'SEND_FAILED',
          message: 'Failed to send data injection message'
        };
        console.warn('VoiceSDK v2: Failed to send data injection message');
        this.emit('dataInjectionError', error);
        reject(new Error(error.message));
      } else {
        console.log('📤 VoiceSDK v2: Sent data_inject message:', {
          correlationId: correlationId,
          dataLength: trimmedData.length,
          source: source
        });
      }
    });
  }

  /**
   * Handle acknowledgment for data injection.
   * Matches correlationId to pending injection and resolves the Promise.
   * 
   * @param {Object} message - The ack message data
   * @fires VoiceSDK#dataInjected - When injection is successfully acknowledged
   */
  handleDataInjectAck(message) {
    const correlationId = message.correlationId;

    if (!correlationId) {
      console.warn('VoiceSDK v2: data_inject_ack received without correlationId');
      return;
    }

    const pending = this.pendingInjections.get(correlationId);
    if (!pending) {
      console.warn('VoiceSDK v2: data_inject_ack received for unknown correlationId:', correlationId);
      return;
    }

    // Remove from pending map
    this.pendingInjections.delete(correlationId);

    // Resolve the Promise
    pending.resolve(message);

    // Emit event for listeners
    this.emit('dataInjected', {
      correlationId: correlationId,
      data: message.data,
      metadata: message.metadata
    });

    console.log('✅ VoiceSDK v2: Data injection acknowledged:', correlationId);
  }

  /**
   * Handle error response for data injection.
   * Matches correlationId to pending injection and rejects the Promise.
   * 
   * @param {Object} message - The error message data
   * @fires VoiceSDK#dataInjectionError - When injection error is received from server
   */
  handleDataInjectError(message) {
    const correlationId = message.correlationId;

    if (!correlationId) {
      console.warn('VoiceSDK v2: data_inject_error received without correlationId');
      return;
    }

    const pending = this.pendingInjections.get(correlationId);
    if (!pending) {
      console.warn('VoiceSDK v2: data_inject_error received for unknown correlationId:', correlationId);
      return;
    }

    // Remove from pending map
    this.pendingInjections.delete(correlationId);

    // Build error object
    const error = new Error(message.message || 'Data injection failed');
    error.error = message.error;
    error.correlationId = correlationId;

    // Reject the Promise
    pending.reject(error);

    // Emit event for listeners
    this.emit('dataInjectionError', {
      error: message.error,
      message: message.message,
      correlationId: correlationId
    });

    console.warn('❌ VoiceSDK v2: Data injection error:', correlationId, message.error);
  }

  

  /**

   * Handle transcript message

   */

  handleTranscript(message) {

    if (!message.sentenceId || !message.text) {

      console.warn('⚠️ VoiceSDK v2: Invalid transcript message:', message);

      return;

    }

    

    const sentenceId = message.sentenceId;

    const transcriptData = {

      text: message.text,

      role: message.role || 'assistant',

      sentenceId: sentenceId,

      final: message.final !== false, // Default to true if not specified

      timestamp: message.timestamp || Date.now()

    };

    

    // Transcripts arrive BETWEEN sentences - they signal that the previous sentence is complete
    // Display the transcript immediately when it arrives (it's for the sentence that just finished)
    this.transcriptQueue.push(transcriptData);
    
    console.log('📝 VoiceSDK v2: Received transcript for sentence', sentenceId, ':', transcriptData.text);
    console.log('   Transcript queue length:', this.transcriptQueue.length);
    console.log('   Current playing sentence:', this.currentPlayingSentenceId);
    
    // Display transcript immediately - it represents the sentence that just completed
    // The transcript arrives between sentences, so when it arrives, the previous sentence is done
    console.log('📝 VoiceSDK v2: Displaying transcript for completed sentence', sentenceId);
    this.emit('transcriptDisplay', transcriptData);
    
    // Update current sentence ID to this transcript's sentence ID
    // The next audio frames will be for the next sentence
    this.currentPlayingSentenceId = sentenceId;
    
    // Emit transcript event for listeners
    this.emit('transcript', transcriptData);

  }

  

  /**

   * Handle binary audio data

   */

  async handleBinaryMessage(data) {

    // Convert Blob to ArrayBuffer if needed

    let arrayBuffer;

    if (data instanceof Blob) {

      arrayBuffer = await data.arrayBuffer();

    } else {

      arrayBuffer = data;

    }

    

    

    // Convert format if needed (before passing to AudioPlayer)
    let processedAudio = arrayBuffer;
    
    if (this.formatConverter && this.formatConverter.needsConversion()) {
      try {
        processedAudio = await this.formatConverter.convert(arrayBuffer);
        console.log('✅ VoiceSDK v2: Audio converted to requested format');
      } catch (error) {
        console.error('❌ VoiceSDK v2: Format conversion failed:', error);
        console.warn('   Using backend format without conversion');
        processedAudio = arrayBuffer; // Fall back to original
      }
    }
    
    // Determine format for playback (use requested format if converter was used, otherwise use negotiated)
    const playbackFormat = this.formatConverter && this.formatConverter.needsConversion() 
      ? this.requestedOutputFormat 
      : (this.outputAudioFormat || {
          container: this.config.outputContainer || 'raw',
          encoding: this.config.outputEncoding || 'pcm',
          sampleRate: this.config.outputSampleRate || 24000,
          bitDepth: this.config.outputBitDepth || 16,
          channels: this.config.outputChannels || 1
        });
    
    const container = playbackFormat.container || 'raw';
    const encoding = (playbackFormat.encoding || 'pcm').toLowerCase();
    
    if (container === 'raw') {
      // Raw audio - decode if needed, then use playChunk for seamless scheduling
      let pcmData = processedAudio;
      
      if (encoding !== 'pcm') {
        // Need to decode PCMU/PCMA to PCM
        const codec = this.audioPlayer.getCodec(encoding);
        if (codec) {
          const decoded = codec.decode(new Uint8Array(processedAudio));
          pcmData = decoded.buffer;
          console.log(`🔄 VoiceSDK v2: Decoded ${encoding.toUpperCase()} to PCM (${decoded.byteLength} bytes)`);
        } else {
          console.warn(`⚠️ VoiceSDK v2: No codec for ${encoding}, treating as PCM`);
        }
      }
      
      // Ensure outputFormat is set on AudioPlayer before playing chunks
      // This is critical for raw PCM playback
      if (!this.audioPlayer.outputFormat) {
        console.warn('⚠️ VoiceSDK v2: outputFormat not set on AudioPlayer, setting from playbackFormat');
        this.audioPlayer.setOutputFormat(playbackFormat);
      }
      
      // Track audio frames
      // Transcripts are displayed when they arrive (between sentences), not when frames arrive
      this.audioFrameCount++;
      
      // Use playChunk for seamless scheduling
      this.audioPlayer.playChunk(pcmData);
    } else {
      // WAV or other - use playAudio which handles headers
      // Track audio frames
      // Transcripts are displayed when they arrive (between sentences), not when frames arrive
      this.audioFrameCount++;
      
      this.audioPlayer.playAudio(processedAudio);
    }

  }

  

  /**
  
   * Send message to server
  
   */
  
  sendMessage(message) {

    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {

      console.warn('VoiceSDK v2: Cannot send message - not connected');

      return false;

    }

    

    try {
      const timestamp = new Date().toISOString();
      const wsState = this.websocket ? this.websocket.readyState : 'NO_WEBSOCKET';
      const wsStateName = this.websocket ? {
        0: 'CONNECTING',
        1: 'OPEN',
        2: 'CLOSING',
        3: 'CLOSED'
      }[wsState] : 'NO_WEBSOCKET';

      // Log client tool result/error messages for debugging
      if (message.t === 'client_tool_result' || message.t === 'client_tool_error') {
        console.log(`📤 VoiceSDK v2: [${timestamp}] Sending tool ${message.t}`);
        console.log(`   🔌 WebSocket state: ${wsStateName} (${wsState})`);
        console.log(`   📋 toolCallId: ${message.toolCallId}`);
        console.log(`   📋 conversationId: ${message.conversationId || 'null'}`);
        console.log(`   📋 toolName: ${message.toolName}`);
        if (message.t === 'client_tool_result') {
          console.log(`   📋 result:`, message.result);
        } else {
          console.log(`   📋 error: ${message.error}, errorCode: ${message.errorCode}`);
        }
        console.log(`   📋 Full message:`, JSON.stringify(message, null, 2));
      }

      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
        console.error(`❌ VoiceSDK v2: Cannot send message - WebSocket not open (state: ${wsStateName})`);
        return false;
      }

      const messageStr = JSON.stringify(message);
      this.websocket.send(messageStr);
      
      if (message.t === 'client_tool_result' || message.t === 'client_tool_error') {
        console.log(`✅ VoiceSDK v2: [${timestamp}] Tool ${message.t} message sent successfully (${messageStr.length} bytes)`);
      }

      return true;

    } catch (error) {

      console.error('VoiceSDK v2: Error sending message:', error);

      this.emit('error', error);

      return false;

    }

  }

  

  /**

   * Send binary audio data to server

   */

  sendBinary(audioData) {

    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {

      console.warn('VoiceSDK v2: Cannot send audio - not connected');

      return false;

    }

    

    try {

      this.websocket.send(audioData);

      return true;

    } catch (error) {

      console.error('VoiceSDK v2: Error sending audio:', error);

      this.emit('error', error);

      return false;

    }

  }

  

  /**
   * Pause the call. Stops sending/receiving audio.
   */
  pauseCall() {
    if (this.isPaused) return;
    this.sendMessage({ t: 'pause_call' });
    // Stop sending audio
    if (this.audioRecorder) {
      this.audioRecorder.stop();
    }
    // Flush AudioWorklet ring buffer
    if (this.audioRecorder?.audioWorkletNode) {
      this.audioRecorder.audioWorkletNode.port.postMessage({ type: 'flush' });
    }
    // Clear audio playback queue
    if (this.audioPlayer) {
      this.audioPlayer.stop();
    }
    this.isPaused = true;
  }

  /**
   * Resume a paused call.
   */
  resumeCall() {
    if (!this.isPaused) return;
    this.sendMessage({ t: 'resume_call' });
    this.isPaused = false;
    // Restart recording immediately (don't wait for server ack)
    this.startRecording();
  }

  /**
   *
   * Gracefully end the call.
   * Sends hangup message to backend before closing WebSocket.
   *
   */
  hangup() {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      // Silent return - WebSocket already closed (expected when server rejects or already disconnected)
      return;
    }

    console.log('📞 VoiceSDK v2: Hanging up call...');

    this._gracefulHangupInitiated = true;

    this.isPaused = false;

    // Clear stored conversationId on graceful hangup
    // This prevents attempting resume after intentional end
    this.clearStoredConversationId();

    // Stop recording first
    this.stopRecording();

    // Release wake lock on hangup
    this._releaseWakeLock();

    // Stop audio playback
    if (this.audioPlayer) {
      this.stopAudioPlayback();
    }

    // Send hangup message
    try {
      this.sendMessage({ t: 'hangup' });
      
      // Close WebSocket after short delay to ensure message is sent
      // Backend will also close from its side after receiving hangup
      setTimeout(() => {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
          this.websocket.close(1000, 'User hangup');
          this.websocket = null;
        }
        this.isConnected = false;
      }, 100);
    } catch (error) {
      console.error('VoiceSDK v2: Error sending hangup message:', error);
      // Close immediately if send fails
      if (this.websocket) {
        this.websocket.close(1000, 'User hangup');
        this.websocket = null;
      }
      this.isConnected = false;
    }
  }


  

  /**

   * Start recording audio from microphone

   * @param {MediaStream} [existingMediaStream] - Optional. Use this stream instead of getUserMedia (iOS fix).

   */

  async startRecording(existingMediaStream = null) {

    if (!this.isConnected) {

      const error = new Error('Not connected to voice server');

      this.emit('error', error);

      throw error;

    }

    

    if (this.isRecording) {

      console.warn('VoiceSDK v2: Already recording');

      return true;

    }

    if (this.disclaimersPending) {

      const error = new Error('Server disclaimers must be accepted before recording. Call sendDisclaimerAck(true) first.');

      error.code = 'DISCLAIMER_PENDING';

      this.emit('error', error);

      throw error;

    }

    

    console.log('🎤 VoiceSDK v2: Starting recording...');

    

    try {

      // CRITICAL for mobile browsers: Request microphone permission FIRST
      // iOS: Pass existingMediaStream to avoid second getUserMedia (can fail on iOS)
      console.log('🎤 VoiceSDK v2: Requesting microphone permission (mobile-friendly)...');
      await this.audioRecorder.start(existingMediaStream ? { existingMediaStream } : {});
      
      // CRITICAL: Check if connection is still valid AFTER getting permission
      // Server might have rejected during the async permission request
      if (!this.isConnected || !this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
        // Silent cleanup - server rejected, this is expected
        // Stop audioRecorder immediately since we just started it
        try {
          await this.audioRecorder.stop();
        } catch (stopError) {
          // Ignore stop errors during cleanup
        }
        const error = new Error('Connection lost - server may have rejected the call');
        error.name = 'ServerRejected';
        error.isServerRejection = true;
        // Don't emit error event - this is expected when server rejects
        // The error handler in VoiceInterface will handle it appropriately
        throw error;
      }
      
      // CRITICAL for mobile: getUserMedia can suspend the playback AudioContext
      // Resume it immediately to prevent audio interruption
      await this.audioPlayer.resumeAudioContext();
      
      // After permission is granted, send start_continuous_mode message to server
      this.sendMessage({

        t: 'start_continuous_mode',

        ttpId: this.generateTtpId()

      });

      // Request screen wake lock to keep screen on during voice call (mobile)
      this._requestWakeLock();

      

      return true;

      

    } catch (error) {

      // CRITICAL: If AudioRecorder started but connection was lost, stop it immediately
      if (this.audioRecorder && this.audioRecorder.isRecording) {
        try {
          await this.audioRecorder.stop();
        } catch (stopError) {
          // Ignore stop errors during cleanup
        }
      }

      // Handle server rejection gracefully (don't log as error, don't emit error event)
      if (error.isServerRejection || error.name === 'ServerRejected') {
        // Server rejection is expected - don't treat as error
        // The error handler in VoiceInterface will handle UI appropriately
        return false;
      }

      // Log and emit other errors (permission denied, etc.)
      console.error('❌ VoiceSDK v2: Failed to start recording:', error);

      // Provide helpful error messages for permission issues
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        const permissionError = new Error('Microphone permission denied. Please allow microphone access in your browser settings and try again.');
        permissionError.name = error.name;
        permissionError.originalError = error;
        this.emit('error', permissionError);
      } else {
        this.emit('error', error);
      }

      return false;

    }

  }

  

  /**

   * Stop recording audio

   */

  async stopRecording() {

    if (!this.isRecording) {

      console.warn('VoiceSDK v2: Not recording');

      return true;

    }

    

    console.log('🛑 VoiceSDK v2: Stopping recording...');

    

    try {

      // Send stop_continuous_mode message to server

      this.sendMessage({

        t: 'stop_continuous_mode',

        ttpId: this.generateTtpId()

      });

      
      // Emit recordingStopped BEFORE stopping the recorder to ensure listeners receive it
      // This is important because the recorder.stop() might be async and listeners might be removed
      this.isRecording = false;
      this.emit('recordingStopped');
      console.log('🛑 VoiceSDK v2: Recording stopped (emitted before audioRecorder.stop())');
      
      // Release screen wake lock when recording stops
      this._releaseWakeLock();
      
      // Stop capturing audio from microphone
      await this.audioRecorder.stop();

      

      // Stop any playing audio

      this.stopAudioPlayback();

      

      return true;

      

    } catch (error) {

      console.error('VoiceSDK v2: Error stopping recording:', error);

      this.emit('error', error);

      return false;

    }

  }

  

  /**

   * Toggle recording on/off

   */

  async toggleRecording() {

    if (this.isRecording) {

      return await this.stopRecording();

    } else {

      return await this.startRecording();

    }

  }

  

  /**

   * Clear transcript state (when audio is stopped/interrupted)

   */

  clearTranscriptState() {

    // Clear transcript state

    this.currentPlayingSentenceId = null;

    // Keep transcriptQueue for history

    console.log('📝 VoiceSDK v2: Cleared transcript state');

  }

  /**
   * Request screen wake lock to keep screen on during voice call (mobile)
   * Similar to how Waze keeps the screen on during navigation
   * @private
   */
  async _requestWakeLock() {
    // Check if Wake Lock API is available
    if (!('wakeLock' in navigator)) {
      console.log('📱 VoiceSDK v2: Wake Lock API not available in this browser');
      return;
    }

    // Add a requesting flag to prevent race conditions
    if (this.wakeLock !== null || this._wakeLockRequesting) {
      console.log('📱 VoiceSDK v2: Wake lock already active or being requested');
      return;
    }

    try {
      this._wakeLockRequesting = true;
      // Request wake lock (screen type keeps screen on)
      this.wakeLock = await navigator.wakeLock.request('screen');
      console.log('📱 VoiceSDK v2: Screen wake lock activated - screen will stay on during call');
      
      // Handle wake lock release (e.g., user switches tabs, locks device)
      this.wakeLock.addEventListener('release', () => {
        console.log('📱 VoiceSDK v2: Wake lock released by system');
        this.wakeLock = null;
      });
    } catch (error) {
      // Wake lock request failed (e.g., permission denied, battery saver mode)
      console.warn('📱 VoiceSDK v2: Failed to request wake lock:', error.message);
      this.wakeLock = null;
      // Don't throw - this is a nice-to-have feature, not critical
    } finally {
      this._wakeLockRequesting = false;
    }
  }

  /**
   * Release screen wake lock
   * @private
   */
  async _releaseWakeLock() {
    if (this.wakeLock === null) {
      return;
    }

    try {
      await this.wakeLock.release();
      this.wakeLock = null;
      console.log('📱 VoiceSDK v2: Screen wake lock released');
    } catch (error) {
      console.warn('📱 VoiceSDK v2: Error releasing wake lock:', error.message);
      this.wakeLock = null;
    }
  }

  /**

   * Stop audio playback

   */

  stopAudioPlayback() {

    this.audioPlayer.stopImmediate();

    this.clearTranscriptState();

  }

  

  /**

   * Handle barge-in (interrupt agent, start listening)

   */

  async handleBargeIn() {

    this.stopAudioPlayback();

    

    if (!this.isRecording) {

      await this.startRecording();

    }

  }

  

  /**

   * Get SDK status

   */

  getStatus() {

    return {

      version: this.version,

      isConnected: this.isConnected,

      isRecording: this.isRecording,

      isPlaying: this.isPlaying,

      outputFormat: this.outputAudioFormat,

      conversationId: this.conversationId,

      audioPlayer: this.audioPlayer.getStatus(),

      audioRecorder: this.audioRecorder.getStatus()

    };

  }

  

  /**

   * Update configuration

   */

  updateConfig(newConfig) {

    this.config = { ...this.config, ...newConfig };

  }

  

  /**

   * Generate unique TTP ID

   */

  generateTtpId() {

    return 'sdk_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();

  }

  /**
   * Generate unique correlation ID for data injection tracking
   * Uses crypto.randomUUID() if available, otherwise falls back to timestamp + counter + random
   * @returns {string} Unique correlation ID
   */
  generateCorrelationId() {
    // Use crypto.randomUUID() if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    
    // Fallback: timestamp + counter + random string
    this.injectionCounter++;
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `inj_${timestamp}_${this.injectionCounter}_${random}`;
  }

  

  /**

   * Destroy SDK and cleanup resources

   */

  /**
   * Register a handler for a client tool
   * @param {string} name - Tool name (must match database definition)
   * @param {Function} handler - Async function that receives parameters and returns result
   */
  registerToolHandler(name, handler) {
    return this.clientToolsRegistry.registerHandler(name, handler);
  }

  /**
   * Unregister a tool handler
   * @param {string} name - Tool name
   */
  unregisterToolHandler(name) {
    return this.clientToolsRegistry.unregisterHandler(name);
  }

  /**
   * Check if a tool handler is registered
   * @param {string} name - Tool name
   * @returns {boolean}
   */
  hasToolHandler(name) {
    return this.clientToolsRegistry.hasHandler(name);
  }

  /**
   * Get all registered tool handler names
   * @returns {string[]}
   */
  getRegisteredTools() {
    return this.clientToolsRegistry.getRegisteredTools();
  }

  destroy() {

    if (this.isDestroyed) return;

    

    console.log('🗑️ VoiceSDK v2: Destroying...');

    

    this.isDestroyed = true;

    // Stop recording
    if (this.isRecording) {
      this.stopRecording();
    }

    // Release wake lock
    this._releaseWakeLock();
    this._wakeLockRequesting = false; // Reset flag

    // Stop audio playback
    if (this.audioPlayer) {
      this.stopAudioPlayback();
    }

    // Close WebSocket (no hangup - this is cleanup, not graceful end)
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    this.isConnected = false;

    // hello_ack calls storeConversationId(), which refreshes RESUME_TTL_MS. If connect
    // then aborts (mic denied, disconnect, etc.) without hangup(), sessionStorage still
    // looks like a fresh in-call navigation and the widget auto-resumes on refresh.
    // Graceful hangup() clears storage and sets _gracefulHangupInitiated — do not wipe here.
    if (this.helloAckReceived && !this._gracefulHangupInitiated) {
      this.clearStoredConversationId();
    }

    // Reject all pending data injections on destroy
    if (this.pendingInjections.size > 0) {
      console.warn(`VoiceSDK v2: Rejecting ${this.pendingInjections.size} pending data injections due to destroy`);
      const error = new Error('SDK destroyed - data injection cancelled');
      error.error = 'SDK_DESTROYED';
      
      this.pendingInjections.forEach((pending, correlationId) => {
        clearTimeout(pending.timeout);
        pending.reject(error);
      });
      
      this.pendingInjections.clear();
    }

    // Cleanup components

    if (this.audioPlayer) {

      this.audioPlayer.destroy();

    }

    

    if (this.audioRecorder) {

      this.audioRecorder.destroy();

    }

    // Close shared AudioContexts so they don't leak on SPA route changes
    AudioPlayer.closeSharedContext();
    AudioRecorder.closeSharedContext();

    
    // Tool handlers persist on the shared registry across SDK lifecycle —
    // do not unregister here, otherwise we clobber handlers owned by the widget
    // (e.g. flavor tools) or by another live SDK (e.g. TextChatSDK).

    this.removeAllListeners();

  }

}



export default VoiceSDK_v2;


